import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import adminApi from '../../Api/adminApi';
import store from '../../redux/store';
import { showError, showSuccess } from '../../redux/actions';
import { COLORS2 } from '../../constants';
import useAdminStyles from './adminStyles';
import { errorMessage, fmtDate } from './adminFormat';

const PLAN_BUTTONS = ['FREE', 'SPECIAL', 'BASIC', 'PREMIUM'];

function sameDay(a, b) {
    if (!a || !b) {
        return !a && !b;
    }
    const x = new Date(a);
    const y = new Date(b);
    return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
}

/**
 * Plan override and expiry controls for one user. Every change is confirmed
 * with the exact effect, validated again on the server, and logged there with
 * the acting admin. Each action returns the refreshed detail via `onUpdated`.
 */
const ManagementActions = ({ detail, onUpdated }) => {
    const classes = useAdminStyles();
    const [busy, setBusy] = useState(false);
    const [endDate, setEndDate] = useState(null);
    const [error, setError] = useState(null);

    const currentEnd = detail?.subscription?.endDate || null;

    useEffect(() => {
        setEndDate(currentEnd ? new Date(currentEnd) : null);
        setError(null);
    }, [detail, currentEnd]);

    const run = (promise) => {
        setBusy(true);
        setError(null);
        return promise
            .then((res) => {
                store.dispatch(showSuccess('ADMIN_USER_UPDATED'));
                onUpdated(res.data);
            })
            .catch((err) => {
                console.error(err);
                setError(errorMessage(err));
                store.dispatch(showError('GENERIC_ERROR'));
            })
            .finally(() => setBusy(false));
    };

    const setPlan = (plan) => {
        const lines = [
            `Set ${detail.email} to ${plan}?`,
            '',
            `Current plan: ${detail.effectivePlan || '—'} (${detail.status || '—'})`,
            'This replaces the plan immediately and clears its end date.',
        ];
        if (detail.subscription?.stripeCustomerSubscriptionId) {
            lines.push('', 'This user has a Stripe subscription id on file; Stripe is NOT changed by this.');
        }
        if (!window.confirm(lines.join('\n'))) {
            return;
        }
        run(adminApi.setUserPlan(detail.id, plan));
    };

    const endDateValid = endDate && !isNaN(endDate.getTime());
    const endDateChanged = endDateValid && !sameDay(endDate, currentEnd);

    const saveEndDate = () => {
        if (!endDateChanged) {
            return;
        }
        const msg = [
            `Change the end date for ${detail.email}?`,
            '',
            `From: ${fmtDate(currentEnd, 'never')}`,
            `To:   ${fmtDate(endDate)}`,
            '',
            'After this date (plus the grace period for paid plans) the user drops to FREE.',
        ].join('\n');
        if (!window.confirm(msg)) {
            return;
        }
        run(adminApi.setUserEndDate(detail.id, endDate.toISOString()));
    };

    const currentPlan = detail?.effectivePlan;

    return (
        <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                {PLAN_BUTTONS.map((plan) => (
                    <Button
                        key={plan}
                        variant={currentPlan === plan ? 'contained' : 'outlined'}
                        color="primary"
                        size="small"
                        disabled={busy || currentPlan === plan}
                        onClick={() => setPlan(plan)}
                    >
                        {plan}
                    </Button>
                ))}
            </div>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
                    <KeyboardDatePicker
                        disableToolbar
                        variant="inline"
                        inputVariant="outlined"
                        size="small"
                        format="dd/MM/yyyy"
                        label="Plan end date"
                        value={endDate}
                        onChange={setEndDate}
                        disabled={busy || currentPlan === 'FREE'}
                        KeyboardButtonProps={{ 'aria-label': 'change end date' }}
                    />
                    <Button variant="outlined" size="small" disabled={busy || !endDateChanged} onClick={saveEndDate}>
                        Update end date
                    </Button>
                </div>
            </MuiPickersUtilsProvider>
            <p className={classes.muted} style={{ margin: '10px 0 0', fontSize: '0.84rem' }}>
                Changes apply immediately, are logged with your account, and do not touch Stripe.
            </p>
            {error ? (
                <p style={{ margin: '8px 0 0', fontSize: '0.88rem', color: COLORS2.virtualRed }} role="alert">
                    {error}
                </p>
            ) : null}
        </div>
    );
};

export default ManagementActions;
